import { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { router } from '@inertiajs/react'
import { useTusUpload } from './useTusUpload'
import { useBulkUploadNotification } from './useBulkUploadNotification'

interface DraggedItem {
  type: 'Document' | 'Folder'
  id: string
  name: string
  currentFolderId?: string | null // For documents: current folder_id
  currentParentId?: string | null // For folders: current parent_id
}

interface DragDropOptions {
  currentFolderId: string | null
  onShowToast: (type: 'info' | 'success' | 'error' | 'warning' | 'loading', title: string, options?: any) => string
  onUpdateToast: (id: string, updates: any) => void
  onDismissToast: (id: string) => void
  t: (key: string, options?: any) => string
}

export function useFileExplorerDragAndDrop({
  currentFolderId,
  onShowToast,
  onUpdateToast,
  onDismissToast,
  t
}: DragDropOptions) {
  const [isDragging, setIsDragging] = useState(false)
  const [dragOverTarget, setDragOverTarget] = useState<string | null>(null)
  const [draggedItems, setDraggedItems] = useState<DraggedItem[]>([])
  const pendingDropFolderIdRef = useRef<string | null | undefined>(undefined)

  // For backwards compatibility, expose single draggedItem
  const draggedItem = draggedItems.length > 0 ? draggedItems[0] : null

  // Use TUS upload hook
  const { files, addFiles, clearAll } = useTusUpload()

  // Use bulk upload notification hook (folder ID will be passed to startTracking)
  const { startTracking } = useBulkUploadNotification({
    files,
    folderId: null, // Will be overridden in startTracking call
    showToast: onShowToast,
    updateToast: onUpdateToast,
    dismissToast: onDismissToast
  })

  // Auto-start tracking when files are added via drag and drop
  useEffect(() => {
    if (pendingDropFolderIdRef.current !== undefined && files.length > 0) {
      // Files have been added, start tracking
      startTracking(pendingDropFolderIdRef.current)
      pendingDropFolderIdRef.current = undefined
    }
  }, [files.length, startTracking])

  // Global event listeners to handle drag cancellation and drops outside zones
  useEffect(() => {
    const handleDragEnd = () => {
      setIsDragging(false)
      setDragOverTarget(null)
      setDraggedItems([])
    }

    const handleDocumentDrop = (e: DragEvent) => {
      // Prevent default to avoid browser opening the file
      e.preventDefault()
      setIsDragging(false)
      setDragOverTarget(null)
      setDraggedItems([])
    }

    const handleDocumentDragOver = (e: DragEvent) => {
      // Prevent default to avoid browser opening the file
      e.preventDefault()
    }

    const handleDocumentDragLeave = (e: DragEvent) => {
      // Only reset if we're leaving the document itself (not just moving to a child element)
      // Check if the related target is outside the document or null (leaving browser window)
      if (!e.relatedTarget || e.relatedTarget === null) {
        setIsDragging(false)
        setDragOverTarget(null)
        setDraggedItems([])
      }
    }

    // Add listeners when component mounts
    document.addEventListener('dragend', handleDragEnd)
    document.addEventListener('drop', handleDocumentDrop)
    document.addEventListener('dragover', handleDocumentDragOver)
    document.addEventListener('dragleave', handleDocumentDragLeave)

    return () => {
      // Cleanup listeners when component unmounts
      document.removeEventListener('dragend', handleDragEnd)
      document.removeEventListener('drop', handleDocumentDrop)
      document.removeEventListener('dragover', handleDocumentDragOver)
      document.removeEventListener('dragleave', handleDocumentDragLeave)
    }
  }, [])

  // Handle file drop
  const handleFileDrop = useCallback((fileList: FileList, targetFolderId: string | null) => {
    // Clear any previous files first
    clearAll()

    // Store the folder ID for when files are added
    pendingDropFolderIdRef.current = targetFolderId

    // Add files to the TUS upload hook (validation happens inside)
    // The hook will automatically start uploading
    // When files are added, the useEffect will detect it and call startTracking
    addFiles(fileList)
  }, [addFiles, clearAll])

  // Move item to a target folder
  const handleMoveItem = useCallback(async (item: DraggedItem, targetFolderId: string | null) => {
    const toastId = onShowToast('loading', t('fileExplorer.move.messages.moving', { name: item.name }), {
      dismissible: false
    })

    try {
      const endpoint = item.type === 'Document' ? `/documents/${item.id}/move` : `/folders/${item.id}/move`

      await new Promise<void>((resolve, reject) => {
        router.patch(
          endpoint,
          { target_folder_id: targetFolderId || undefined },
          {
            except: ['storage_usage'],
            onSuccess: () => resolve(),
            onError: (errors) => reject(errors),
            preserveScroll: true,
          }
        )
      })

      onDismissToast(toastId)
      // Success toast will be shown via flash message from server
    } catch (error) {
      onDismissToast(toastId)
      console.error('Move error:', error)
      // Error toast will be shown via flash message from server
    }
  }, [onShowToast, onDismissToast, t])

  // Move multiple items to a target folder
  const handleBulkMoveItems = useCallback(async (items: DraggedItem[], targetFolderId: string | null) => {
    const toastId = onShowToast('loading', t('fileExplorer.bulkActions.messages.moving', { count: items.length }), {
      dismissible: false
    })

    try {
      const itemsPayload = items.map(item => ({
        type: item.type,
        id: item.id
      }))

      await new Promise<void>((resolve, reject) => {
        router.post(
          '/bulk_move',
          {
            items: itemsPayload,
            target_folder_id: targetFolderId || undefined,
            current_folder_id: currentFolderId || undefined
          },
          {
            except: ['storage_usage'],
            onSuccess: () => resolve(),
            onError: (errors) => reject(errors),
            preserveScroll: true,
          }
        )
      })

      onDismissToast(toastId)
      // Success/warning/error toast will be shown via flash message from server

      // Clear selection after successful move
      const { useSelectionStore } = await import('../../../stores/useSelectionStore')
      useSelectionStore.getState().clear()
    } catch (error) {
      onDismissToast(toastId)
      console.error('Bulk move error:', error)
      // Error toast will be shown via flash message from server
    }
  }, [currentFolderId, onShowToast, onDismissToast, t])

  // Item drag handlers
  const handleItemDragStart = useCallback((items: DraggedItem | DraggedItem[]) => {
    // Support both single item and array of items
    const itemsArray = Array.isArray(items) ? items : [items]
    setDraggedItems(itemsArray)
    setIsDragging(true)
  }, [])

  const handleItemDragEnd = useCallback(() => {
    setDraggedItems([])
    setIsDragging(false)
    setDragOverTarget(null)
  }, [])

  // Drag event handlers (for file uploads from OS)
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()

    // Only set isDragging for file uploads (not for item moves)
    if (e.dataTransfer.types.includes('Files') && draggedItems.length === 0) {
      setIsDragging(true)
    }
  }, [draggedItems])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()

    // Only set to false if leaving the main drag area
    if (e.currentTarget === e.target) {
      setIsDragging(false)
      setDragOverTarget(null)
    }
  }, [])

  const handleDrop = useCallback((e: React.DragEvent, targetFolderId: string | null = null) => {
    e.preventDefault()
    e.stopPropagation()

    setIsDragging(false)
    setDragOverTarget(null)

    // Check if we're dragging items to move them
    if (draggedItems.length > 0) {
      const folderId = targetFolderId !== null ? targetFolderId : currentFolderId

      // Filter out items that are already in the target location
      const itemsToMove = draggedItems.filter(item => {
        const itemCurrentFolderId = item.type === 'Document'
          ? (item as any).currentFolderId
          : (item as any).currentParentId
        return itemCurrentFolderId !== folderId
      })

      if (itemsToMove.length === 0) {
        // All items are already in this folder, don't trigger move
        setDraggedItems([])
        return
      }

      // Use bulk move if multiple items, single move otherwise
      if (itemsToMove.length > 1) {
        handleBulkMoveItems(itemsToMove, folderId)
      } else {
        handleMoveItem(itemsToMove[0], folderId)
      }

      setDraggedItems([])
      return
    }

    // Otherwise, handle file upload from OS
    const files = e.dataTransfer.files
    if (files.length > 0) {
      // Use target folder ID if provided, otherwise use current folder
      const folderId = targetFolderId !== null ? targetFolderId : currentFolderId
      handleFileDrop(files, folderId)
    }
  }, [currentFolderId, draggedItems, handleFileDrop, handleMoveItem, handleBulkMoveItems])

  // Set drag over target (for highlighting)
  const setDragTarget = useCallback((target: string | null) => {
    setDragOverTarget(target)
  }, [])

  // Memoize the return object to prevent unnecessary re-renders
  return useMemo(() => ({
    isDragging,
    dragOverTarget,
    draggedItem, // For backwards compatibility
    draggedItems, // For multi-item drag feedback
    handleDragEnter,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    setDragTarget,
    handleItemDragStart,
    handleItemDragEnd
  }), [
    isDragging,
    dragOverTarget,
    draggedItem,
    draggedItems,
    handleDragEnter,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    setDragTarget,
    handleItemDragStart,
    handleItemDragEnd
  ])
}
